import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Alert,
  Avatar,
  Chip,
  Tooltip,
  Paper,
} from '@mui/material';
import { Search, CheckCircle, Cancel, PhotoCamera, HourglassEmpty } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';

interface MemberEntry {
  _id: string;
  user: {
    _id: string;
    name?: string;
    email?: string;
    phoneNumber?: string;
    image?: string;
    gender?: string;
    age?: number;
  };
  formSubmitted: boolean;
  hasInjuries: boolean | null;
  injuryDetails?: string | null;
  mediaConsent: boolean | null;
  movementDeclaration?: boolean | null;
  wellnessDeclaration?: boolean | null;
  isPregnant?: boolean | null;
  recentSurgery?: boolean | null;
  healthDeclaration?: boolean | null;
  liabilityWaiver?: boolean | null;
  termsAccepted?: boolean | null;
  createdAt: string | null;
  updatedAt?: string | null;
}

interface StatsData {
  totalMembers: number;
  totalSubmitted: number;
  totalPending: number;
}

const HealthFormsPage: React.FC = () => {
  const [rows, setRows] = useState<MemberEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<StatsData>({ totalMembers: 0, totalSubmitted: 0, totalPending: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const fetchForms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getStudioHealthForms({
        page: page + 1,
        limit: pageSize,
        ...(search ? { search } : {}),
      });
      setRows(res?.submissions || []);
      setTotal(res?.total || 0);
      setStats({
        totalMembers: res?.totalMembers ?? 0,
        totalSubmitted: res?.totalSubmitted ?? 0,
        totalPending: res?.totalPending ?? 0,
      });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load health forms');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    const timer = setTimeout(fetchForms, search ? 400 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchForms]);

  const columns: GridColDef[] = [
    {
      field: 'user',
      headerName: 'Member',
      width: 250,
      renderCell: (params) => {
        const user = params.row.user;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', overflow: 'hidden' }}>
            <Avatar src={user?.image} sx={{ width: 34, height: 34, flexShrink: 0, bgcolor: colors.primary, fontSize: 14 }}>
              {(user?.name || 'U')[0].toUpperCase()}
            </Avatar>
            <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {user?.name || 'Unknown'}
              </Typography>
              {user?.email && (
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {user.email}
                </Typography>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.user?.phoneNumber || '—'}</Typography>
      ),
    },
    {
      field: 'gender',
      headerName: 'Gender',
      width: 90,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
          {params.row.user?.gender || '—'}
        </Typography>
      ),
    },
    {
      field: 'age',
      headerName: 'Age',
      width: 70,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.user?.age || '—'}</Typography>
      ),
    },
    {
      field: 'formSubmitted',
      headerName: 'Form Status',
      width: 140,
      renderCell: (params) =>
        params.row.formSubmitted ? (
          <Chip
            icon={<CheckCircle sx={{ fontSize: 14 }} />}
            label="Submitted"
            size="small"
            color="success"
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
        ) : (
          <Chip
            icon={<HourglassEmpty sx={{ fontSize: 14 }} />}
            label="Pending"
            size="small"
            color="warning"
            variant="outlined"
          />
        ),
    },
    {
      field: 'hasInjuries',
      headerName: 'Injuries',
      width: 110,
      renderCell: (params) => {
        if (!params.row.formSubmitted) return <Typography variant="body2" color="text.disabled">—</Typography>;
        return params.row.hasInjuries ? (
          <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Yes" size="small" color="error" variant="outlined" />
        ) : (
          <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="No" size="small" color="success" variant="outlined" />
        );
      },
    },
    {
      field: 'injuryDetails',
      headerName: 'Injury Details',
      width: 210,
      renderCell: (params) => {
        if (!params.row.formSubmitted) return <Typography variant="body2" color="text.disabled">—</Typography>;
        const details = params.row.injuryDetails;
        if (!details) return <Typography variant="body2" color="text.secondary">—</Typography>;
        return (
          <Tooltip title={details} placement="top">
            <Typography
              variant="body2"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }}
            >
              {details}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'mediaConsent',
      headerName: 'Media Consent',
      width: 145,
      renderCell: (params) => {
        if (!params.row.formSubmitted) return <Typography variant="body2" color="text.disabled">—</Typography>;
        return params.row.mediaConsent ? (
          <Chip icon={<PhotoCamera sx={{ fontSize: 14 }} />} label="Granted" size="small" color="success" variant="outlined" />
        ) : (
          <Chip label="Declined" size="small" color="default" variant="outlined" />
        );
      },
    },
    {
      field: 'isPregnant',
      headerName: 'Pregnant',
      width: 100,
      renderCell: (params) => {
        if (!params.row.formSubmitted || params.row.isPregnant == null)
          return <Typography variant="body2" color="text.disabled">—</Typography>;
        return params.row.isPregnant ? (
          <Chip label="Yes" size="small" color="warning" variant="outlined" />
        ) : (
          <Chip label="No" size="small" color="success" variant="outlined" />
        );
      },
    },
    {
      field: 'recentSurgery',
      headerName: 'Recent Surgery',
      width: 130,
      renderCell: (params) => {
        if (!params.row.formSubmitted || params.row.recentSurgery == null)
          return <Typography variant="body2" color="text.disabled">—</Typography>;
        return params.row.recentSurgery ? (
          <Chip label="Yes" size="small" color="warning" variant="outlined" />
        ) : (
          <Chip label="No" size="small" color="success" variant="outlined" />
        );
      },
    },
    {
      field: 'movementDeclaration',
      headerName: 'Movement Decl.',
      width: 130,
      renderCell: (params) => {
        if (!params.row.formSubmitted || params.row.movementDeclaration == null)
          return <Typography variant="body2" color="text.disabled">—</Typography>;
        return params.row.movementDeclaration ? (
          <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Agreed" size="small" color="success" variant="outlined" />
        ) : (
          <Chip label="Not agreed" size="small" color="default" variant="outlined" />
        );
      },
    },
    {
      field: 'wellnessDeclaration',
      headerName: 'Wellness Decl.',
      width: 130,
      renderCell: (params) => {
        if (!params.row.formSubmitted || params.row.wellnessDeclaration == null)
          return <Typography variant="body2" color="text.disabled">—</Typography>;
        return params.row.wellnessDeclaration ? (
          <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Agreed" size="small" color="success" variant="outlined" />
        ) : (
          <Chip label="Not agreed" size="small" color="default" variant="outlined" />
        );
      },
    },
    {
      // Chakra's rider declaration. A studio only ever sees the pair its own
      // form collects — the other pair stays "—", because a submission that
      // never asked the question must not read as a refusal.
      field: 'healthDeclaration',
      headerName: 'Health Decl.',
      width: 130,
      renderCell: (params) => {
        if (!params.row.formSubmitted || params.row.healthDeclaration == null)
          return <Typography variant="body2" color="text.disabled">—</Typography>;
        return params.row.healthDeclaration ? (
          <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Agreed" size="small" color="success" variant="outlined" />
        ) : (
          <Chip label="Not agreed" size="small" color="default" variant="outlined" />
        );
      },
    },
    {
      field: 'liabilityWaiver',
      headerName: 'Liability Waiver',
      width: 140,
      renderCell: (params) => {
        if (!params.row.formSubmitted || params.row.liabilityWaiver == null)
          return <Typography variant="body2" color="text.disabled">—</Typography>;
        return params.row.liabilityWaiver ? (
          <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Agreed" size="small" color="success" variant="outlined" />
        ) : (
          <Chip label="Not agreed" size="small" color="default" variant="outlined" />
        );
      },
    },
    {
      // Vyana's combined declaration & studio policies — one "I agree". Other
      // studios never ask it, so their rows show "—".
      field: 'termsAccepted',
      headerName: 'Declaration & Policies',
      width: 170,
      renderCell: (params) => {
        if (!params.row.formSubmitted || params.row.termsAccepted == null)
          return <Typography variant="body2" color="text.disabled">—</Typography>;
        return params.row.termsAccepted ? (
          <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Agreed" size="small" color="success" variant="outlined" />
        ) : (
          <Chip label="Not agreed" size="small" color="default" variant="outlined" />
        );
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Last Signed',
      width: 150,
      renderCell: (params) => {
        const lastSigned = params.row.updatedAt || params.row.createdAt;
        if (!params.row.formSubmitted || !lastSigned)
          return <Typography variant="body2" color="text.disabled">—</Typography>;
        return (
          <Typography variant="body2">
            {new Date(lastSigned).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </Typography>
        );
      },
    },
  ];

  const gridRows = rows.map((r) => ({ ...r, id: r._id }));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Health Forms
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track health form submissions from your studio members
        </Typography>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, minWidth: 130 }}>
          <Typography variant="h6" fontWeight={700}>{stats.totalMembers}</Typography>
          <Typography variant="caption" color="text.secondary">Total Members</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, minWidth: 130, borderColor: 'success.light' }}>
          <Typography variant="h6" fontWeight={700} color="success.main">{stats.totalSubmitted}</Typography>
          <Typography variant="caption" color="text.secondary">Submitted</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, minWidth: 130, borderColor: 'warning.light' }}>
          <Typography variant="h6" fontWeight={700} color="warning.main">{stats.totalPending}</Typography>
          <Typography variant="caption" color="text.secondary">Pending</Typography>
        </Paper>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <TextField
            placeholder="Search by name, email or phone…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            size="small"
            sx={{ mb: 2, width: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          <DataGrid
            rows={gridRows}
            columns={columns}
            rowCount={total}
            loading={loading}
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            pageSizeOptions={[25, 50, 100]}
            rowHeight={64}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8f9fa' },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
                alignItems: 'center',
                display: 'flex',
              },
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default HealthFormsPage;
